import { Link, useNavigate } from 'react-router-dom';
import { Brain, User, LogOut, Menu, X, ChevronDown, Contrast } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useAuthStore } from '../../store/authStore';
import { useKidsStore } from '../../store/kidsStore';
import { authService } from '../../services/authService';
import { useAccessibility } from '../../contexts/AccessibilityContext';
import { toast } from 'sonner';

export default function Header() {
  const { isAuthenticated, user, profile, logout } = useAuthStore();
  const { selectedKid, logoutKid } = useKidsStore();
  const { highContrast, toggleHighContrast } = useAccessibility();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isKidsMenuOpen, setIsKidsMenuOpen] = useState(false);
  const kidsMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Cerrar menú kids al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (kidsMenuRef.current && !kidsMenuRef.current.contains(event.target as Node)) {
        setIsKidsMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  const handleLogout = async () => {
    try {
      await authService.logout();
      logout();
      navigate('/');
      toast.success('Sesión cerrada correctamente');
    } catch (error) {
      toast.error('Error al cerrar sesión');
    }
  };
  
  const avatar = profile?.avatar || '🧠';
  
  return (
    <header className="sticky top-0 z-50 bg-neureka-purple-dark border-b border-white/10 backdrop-blur-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <Brain className="w-8 h-8 text-neureka-yellow group-hover:scale-110 transition-transform" />
            <div className="flex flex-col">
              <span className="font-display text-xl font-bold text-white">Neureka</span>
              <span className="text-xs text-neureka-yellow">Despierta tu mente</span>
            </div>
          </Link>
          
          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-6">
            {isAuthenticated ? (
              <>
                <Link to="/juegos" className="text-white/80 hover:text-white font-medium transition-colors">
                  🎮 Juegos
                </Link>
                <Link to="/rutina" className="text-white/80 hover:text-white font-medium transition-colors">
                  📅 Mi Rutina
                </Link>
                <Link to="/mi-progreso" className="text-white/80 hover:text-white font-medium transition-colors">
                  📊 Mi Progreso
                </Link>
                {/* Kids dropdown */}
                <div className="relative" ref={kidsMenuRef}>
                  <button
                    onClick={() => setIsKidsMenuOpen(!isKidsMenuOpen)}
                    className="flex items-center gap-1 text-white/80 hover:text-white font-medium transition-colors"
                  >
                    {selectedKid ? (
                      <>
                        <span>{selectedKid.avatar}</span>
                        <span className="text-neureka-yellow">{selectedKid.nombre}</span>
                      </>
                    ) : (
                      <span>👶 Kids</span>
                    )}
                    <ChevronDown className="w-3 h-3 mt-0.5" />
                  </button>

                  {isKidsMenuOpen && (
                    <div className="absolute left-0 mt-2 w-52 bg-neureka-purple rounded-lg shadow-xl border border-white/20 overflow-hidden z-50">
                      <Link
                        to="/kids"
                        className="flex items-center gap-2 px-4 py-3 hover:bg-white/10 transition-colors text-sm"
                        onClick={() => setIsKidsMenuOpen(false)}
                      >
                        👨‍👩‍👧‍👦 Gestión de perfiles
                      </Link>
                      <Link
                        to="/kids/selector"
                        className="flex items-center gap-2 px-4 py-3 hover:bg-white/10 transition-colors text-sm"
                        onClick={() => setIsKidsMenuOpen(false)}
                      >
                        🎮 Entrar en Kids
                      </Link>
                      {selectedKid && (
                        <button
                          onClick={async () => {
                            await logoutKid();
                            setIsKidsMenuOpen(false);
                            toast.success(`Perfil de ${selectedKid.nombre} cerrado`);
                          }}
                          className="flex items-center gap-2 w-full px-4 py-3 hover:bg-white/10 transition-colors text-sm text-left border-t border-white/10"
                        >
                          🚪 Salir de {selectedKid.nombre}
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {user?.rol === 'profesional' && (
                  <>
                    <Link to="/profesional" className="text-white/80 hover:text-white font-medium transition-colors">
                      👨‍⚕️ Panel Profesional
                    </Link>
                    <Link to="/profesional/estadisticas" className="text-white/80 hover:text-white font-medium transition-colors">
                      📈 Estadísticas
                    </Link>
                  </>
                )}
                {user?.rol === 'admin' && (
                  <>
                    <Link to="/admin" className="text-white/80 hover:text-white font-medium transition-colors">
                      🛡️ Admin
                    </Link>
                    <Link to="/admin/estadisticas" className="text-white/80 hover:text-white font-medium transition-colors">
                      📊 Estadísticas
                    </Link>
                  </>
                )}

                {/* Toggle alto contraste */}
                <button
                  onClick={toggleHighContrast}
                  title={highContrast ? 'Desactivar alto contraste' : 'Activar alto contraste'}
                  className={`p-2 rounded-lg transition-colors ${
                    highContrast
                      ? 'bg-neureka-yellow text-black'
                      : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  <Contrast className="w-5 h-5" />
                </button>

                {/* User menu */}
                <div className="relative">
                  <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/10 border border-white/20 hover:bg-white/20 transition-colors"
                  >
                    <span className="text-xl">{avatar}</span>
                    <span className="font-semibold text-sm">{user?.username}</span>
                  </button>
                  
                  {isMenuOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-neureka-purple rounded-lg shadow-xl border border-white/20 overflow-hidden">
                      <Link
                        to="/perfil"
                        className="flex items-center gap-2 px-4 py-3 hover:bg-white/10 transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <User className="w-4 h-4" />
                        <span>Perfil</span>
                      </Link>
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsMenuOpen(false);
                        }}
                        className="flex items-center gap-2 w-full px-4 py-3 hover:bg-white/10 transition-colors text-left"
                      >
                        <LogOut className="w-4 h-4" />
                        <span>Cerrar sesión</span>
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <Link to="/login" className="text-white/80 hover:text-white font-medium transition-colors">
                  Iniciar sesión
                </Link>
                <Link to="/register" className="btn-neureka">
                  Registrarse
                </Link>
              </>
            )}
          </nav>
          
          {/* Mobile menu button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/10 bg-neureka-purple-dark">
            <nav className="flex flex-col py-4">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/juegos"
                    className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    🎮 Juegos
                  </Link>
                  <Link
                    to="/rutina"
                    className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    📅 Mi Rutina
                  </Link>
                  <Link
                    to="/mi-progreso"
                    className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    📊 Mi Progreso
                  </Link>
                  {/* Kids móvil */}
                  <div className="border-t border-white/10">
                    <div className="px-4 py-2 text-xs text-white/40 uppercase tracking-wider">
                      👶 Kids
                      {selectedKid && <span className="ml-2 text-neureka-yellow normal-case">({selectedKid.avatar} {selectedKid.nombre})</span>}
                    </div>
                    <Link
                      to="/kids"
                      className="px-6 py-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      👨‍👩‍👧‍👦 Gestión de perfiles
                    </Link>
                    <Link
                      to="/kids/selector"
                      className="px-6 py-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors flex items-center gap-2"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      🎮 Entrar en Kids
                    </Link>
                    {selectedKid && (
                      <button
                        onClick={async () => {
                          await logoutKid();
                          setIsMobileMenuOpen(false);
                          toast.success(`Perfil de ${selectedKid.nombre} cerrado`);
                        }}
                        className="px-6 py-2.5 text-white/80 hover:text-white hover:bg-white/10 transition-colors w-full text-left flex items-center gap-2"
                      >
                        🚪 Salir de {selectedKid.nombre}
                      </button>
                    )}
                  </div>
                  {user?.rol === 'profesional' && (
                    <>
                      <Link
                        to="/profesional"
                        className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        👨‍⚕️ Panel Profesional
                      </Link>
                      <Link
                        to="/profesional/estadisticas"
                        className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        📈 Estadísticas
                      </Link>
                    </>
                  )}
                  {user?.rol === 'admin' && (
                    <>
                      <Link
                        to="/admin"
                        className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        🛡️ Admin
                      </Link>
                      <Link
                        to="/admin/estadisticas"
                        className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        📊 Estadísticas
                      </Link>
                    </>
                  )}
                  <Link
                    to="/perfil"
                    className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors border-t border-white/10"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <User className="w-4 h-4 inline mr-2" />
                    Perfil ({user?.username})
                  </Link>
                  {/* Toggle alto contraste móvil */}
                  <button
                    onClick={toggleHighContrast}
                    className="flex items-center justify-between px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors w-full text-left"
                  >
                    <span className="flex items-center gap-2">
                      <Contrast className="w-4 h-4" />
                      Alto contraste
                    </span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${highContrast ? 'bg-neureka-yellow text-black' : 'bg-white/20 text-white'}`}>
                      {highContrast ? 'ON' : 'OFF'}
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors text-left"
                  >
                    <LogOut className="w-4 h-4 inline mr-2" />
                    Cerrar sesión
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="px-4 py-3 text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Iniciar sesión
                  </Link>
                  <Link
                    to="/register"
                    className="mx-4 my-2 px-4 py-3 bg-neureka-yellow text-neureka-purple font-bold rounded-lg hover:bg-yellow-400 transition-colors text-center"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Registrarse
                  </Link>
                </>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
